import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Search, Star } from 'lucide-react';
import { apiClient } from '../apiClient';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';

function FavoritePromptsPage() {
  const [favorites, setFavorites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [favData, catData] = await Promise.all([
          apiClient.get('/api/favorites/list'),
          apiClient.get('/api/categories')
        ]);
        setFavorites(Array.isArray(favData) ? favData : []);
        setCategories(Array.isArray(catData) ? catData : []);
      } catch (err) {
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categoryMap = useMemo(() => {
    const map = new Map();
    categories.forEach((cat) => {
      map.set(cat.category_id, cat.name);
    });
    return map;
  }, [categories]);

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? favorites.filter((prompt) => {
          const name = (prompt.name || '').toLowerCase();
          const desc = richTextToPlainText(prompt.description || '').toLowerCase();
          return name.includes(term) || desc.includes(term);
        })
      : favorites;

    const groups = {};
    filtered.forEach((prompt) => {
      const categoryId = prompt.category_id || 'uncategorized';
      if (!groups[categoryId]) {
        groups[categoryId] = [];
      }
      groups[categoryId].push(prompt);
    });

    return groups;
  }, [favorites, search]);

  const orderedCategories = useMemo(() => {
    const used = Object.keys(grouped);
    const order = [];
    categories.forEach((cat) => {
      if (used.includes(cat.category_id)) {
        order.push(cat.category_id);
      }
    });
    if (used.includes('uncategorized')) {
      order.push('uncategorized');
    }
    return order;
  }, [categories, grouped]);

  useEffect(() => {
    if (!orderedCategories.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      orderedCategories.forEach((id) => {
        if (next[id] === undefined) {
          next[id] = true;
        }
      });
      return next;
    });
  }, [orderedCategories]);

  const toggleCategory = (categoryId) => {
    setExpanded((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const renderPromptCard = (prompt) => (
    <div
      key={prompt.prompt_id}
      className="card-glass cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/prompts/${prompt.prompt_id}`, {
        state: {
          backTo: '/favorites',
          breadcrumbs: [{ label: 'Favorite Prompts', to: '/favorites' }]
        }
      })}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/prompts/${prompt.prompt_id}`, {
            state: {
              backTo: '/favorites',
              breadcrumbs: [{ label: 'Favorite Prompts', to: '/favorites' }]
            }
          });
        }
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-bold text-lg">{prompt.name}</h3>
        <div className="flex items-center gap-2">
          <VisibilityIndicator visibility={prompt.visibility || 'private'} />
          <span className="badge">{prompt.visibility || 'private'}</span>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-4 line-clamp-2">
        {richTextToPlainText(prompt.description) || 'No description'}
      </p>
      <div className="flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4" />
            {Number(prompt.average_rating ?? 0).toFixed(1)}
          </span>
          {prompt.favorited_at && (
            <span>Saved {new Date(prompt.favorited_at).toLocaleDateString()}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Favorite Prompts</h1>
            <p className="text-gray-400">Your saved prompt inspiration, organized by category.</p>
          </div>
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search favorites"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : orderedCategories.length === 0 ? (
          <div className="card-glass text-center py-12">
            <p className="text-gray-400 mb-4">No favorites yet.</p>
            <button className="btn-primary" onClick={() => navigate('/profile')}>
              Back to Profile
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {orderedCategories.map((categoryId) => {
              const prompts = grouped[categoryId] || [];
              const label = categoryId === 'uncategorized'
                ? 'Uncategorized'
                : (categoryMap.get(categoryId) || 'Category');
              const isOpen = expanded[categoryId];

              return (
                <section key={categoryId} className="favorite-section">
                  <button
                    type="button"
                    className="favorite-header"
                    onClick={() => toggleCategory(categoryId)}
                  >
                    <div>
                      <div className="favorite-title">{label}</div>
                      <div className="favorite-sub">{prompts.length} prompts</div>
                    </div>
                    <ChevronDown className={`w-5 h-5 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                      {prompts.map(renderPromptCard)}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default FavoritePromptsPage;
